import { useState } from 'react';

/**
 * Actionable help shown when the target site blocks automated measurement
 * (reachability preflight failed / every measurement source timed out).
 * Explains WHO to contact and WHAT exactly to ask for, with a ready-to-send
 * message the user can copy for the site's operator/hosting admin.
 *
 * The whitelisting instructions build on the app's existing bot identity:
 * every lab request carries the User-Agent suffix
 * "ControlVitals/1.0 (+https://controlvitals.cz/bot)" and the public bot
 * info page /bot — so a UA-based firewall rule is stable, unlike IP lists.
 */
const BOT_UA = 'ControlVitals/1.0 (+https://controlvitals.cz/bot)';
const BOT_URL = 'https://controlvitals.cz/bot';
const SUPPORT = 'support@controlvitals.cz';

const buildOperatorMessage = (cs, url) => cs
  ? `Dobrý den,

pro web ${url || ''} měříme Core Web Vitals nástrojem ControlVitals. Server ale naše měřicí požadavky blokuje — spojení vyprší dřív, než přijde odpověď (blokace na úrovni firewallu/WAF, ne aplikace).

Prosíme o povolení automatizovaného měření:

1. Ve firewallu / WAF (Cloudflare, CDN77, vlastní řešení…) přidejte výjimku pro požadavky, jejichž User-Agent obsahuje řetězec „ControlVitals“.
   Plná identita bota: ${BOT_UA}
2. Bot respektuje robots.txt (User-agent: ControlVitals) a podrobnosti o něm najdete na ${BOT_URL}.
3. Whitelist podle IP adres nedoporučujeme — adresy měřicí infrastruktury se mohou měnit; pravidlo podle User-Agentu je stabilní. V případě dotazů nás kontaktujte na ${SUPPORT}.

Děkujeme!`
  : `Hello,

we measure Core Web Vitals for ${url || ''} using ControlVitals. Your server is currently blocking our measurement requests — connections time out before any response (firewall/WAF-level blocking, not the application).

Please allow automated measurement:

1. In your firewall / WAF (Cloudflare, CDN, custom…) add an exception for requests whose User-Agent contains the string "ControlVitals".
   Full bot identity: ${BOT_UA}
2. The bot honours robots.txt (User-agent: ControlVitals); details at ${BOT_URL}.
3. We do not recommend IP-based whitelisting — measurement infrastructure IPs can change; a User-Agent rule is stable. Questions: ${SUPPORT}.

Thank you!`;

const BlockedSiteHelp = ({ cs, url }) => {
  const [copied, setCopied] = useState(false);
  const message = buildOperatorMessage(cs, url);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* clipboard unavailable — text stays selectable below */ }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-left">
      <div className="text-xs font-semibold text-amber-300 mb-1">
        {cs ? 'Web blokuje automatizované měření — co s tím?' : 'The site blocks automated measurement — what now?'}
      </div>
      <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4">
        <li>{cs
          ? 'Blokace je na straně cílového webu (firewall/WAF) — typicky postihuje veškerý serverový provoz, často i měření Google PageSpeed.'
          : 'The block is on the target site\u2019s side (firewall/WAF) — it typically affects all server traffic, often including Google PageSpeed.'}</li>
        <li>{cs
          ? <>Správce webu může povolit výjimku podle User-Agentu <span className="font-mono text-amber-200">ControlVitals</span> — je stabilní, na rozdíl od IP adres.</>
          : <>The site admin can whitelist by the User-Agent <span className="font-mono text-amber-200">ControlVitals</span> — stable, unlike IP addresses.</>}</li>
        <li>{cs
          ? <>Podrobnosti o měřicím botu: <a href={BOT_URL} target="_blank" rel="noreferrer" className="underline text-amber-200">{BOT_URL}</a></>
          : <>Measurement bot details: <a href={BOT_URL} target="_blank" rel="noreferrer" className="underline text-amber-200">{BOT_URL}</a></>}</li>
      </ul>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleCopy}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 transition-colors"
        >
          {copied
            ? (cs ? 'Zkopírováno ✓' : 'Copied ✓')
            : (cs ? 'Zkopírovat zprávu pro správce webu' : 'Copy message for the site admin')}
        </button>
        <a
          href={`mailto:?subject=${encodeURIComponent(cs
            ? `Povolení měření Core Web Vitals pro ${url || 'web'}`
            : `Allow Core Web Vitals measurement for ${url || 'the site'}`)}&body=${encodeURIComponent(message)}`}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 transition-colors"
        >
          {cs ? 'Otevřít jako e-mail' : 'Open as e-mail'}
        </a>
      </div>
      <details className="mt-2">
        <summary className="text-[11px] text-slate-400 cursor-pointer">
          {cs ? 'Zobrazit text zprávy' : 'Show message text'}
        </summary>
        <pre className="mt-1 p-2 rounded bg-black/30 text-[11px] text-slate-300 whitespace-pre-wrap select-all">{message}</pre>
      </details>
    </div>
  );
};

export default BlockedSiteHelp;
